import { memo } from 'react';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form-latest';
import type { FieldArrayWithId } from 'react-hook-form-latest';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _notNil } from '@/littledash';
import { preventNumberScroll } from '@/helpers';
import { getSexOptions } from '@/model/Animal.utils';
import { DateUtils } from '@/utils/Date.utils';
import { ErrorMessage } from '@hookform/error-message';
import { featuresSelector } from '@/support/Selectors';
import DatePickerNative from '@/components/UI/DatePickerNative';

import { validationSchema } from './Cages.utils';
import type { CageFormEntry } from './Cages';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface CageRowProps {
  field: FieldArrayWithId<{ collections: Array<CageFormEntry> }, 'collections'>;
  index: number;
}

const CageRow: FC<CageRowProps> = memo(({ field, index }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<{ collections: Array<CageFormEntry> }>();
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const sexOptions = getSexOptions(unknownSexEnabled);

  return (
    <div
      className={cn('w-100 flex flex-wrap justify-between items-start ph4 pv3', {
        'bt b--moon-gray': index !== 0,
      })}
      data-testid="cage-container"
    >
      <div>
        <label htmlFor={`collections.${index}.name`}>Name</label>
        <input
          type="text"
          id={`collections.${index}.name`}
          data-testid={`cage-${index}-name-input`}
          className={cn({ input__error: _notNil(errors?.collections?.[index]?.name) })}
          style={{ marginBottom: 0 }}
          defaultValue={field.name}
          {...register(`collections.${index}.name`, { ...validationSchema.collections.name })}
        />
        <ErrorMessage
          errors={errors}
          name={`collections.${index}.name`}
          render={({ message }) => <p className="f7 red db lh-copy pt1">{message}</p>}
        />
      </div>
      <div>
        <label htmlFor={`collections.${index}.subjects`}>Subjects</label>
        <input
          type="number"
          id={`collections.${index}.subjects`}
          onWheel={preventNumberScroll}
          data-testid={`cage-${index}-subject-count-input`}
          className={cn({ input__error: _notNil(errors?.collections?.[index]?.subjects) })}
          style={{ marginBottom: 0, width: 120 }}
          defaultValue={field.subjects}
          {...register(`collections.${index}.subjects`, {
            ...validationSchema.collections.subjects,
            valueAsNumber: true,
          })}
        />
        <ErrorMessage
          errors={errors}
          name={`collections.${index}.subjects`}
          render={({ message }) => <p className="f7 red db lh-copy pt1">{message}</p>}
        />
      </div>
      <div>
        <label>Sex</label>
        {sexOptions.map(({ label, value }) => (
          <div key={value} className="flex">
            <input
              id={`collections.${index}.sex_${value}`}
              type="radio"
              data-testid={`cage-${index}-sex-${value}-input`}
              value={value}
              className="mr2 dib"
              style={{ marginBottom: 0 }}
              defaultChecked={field.sex === value}
              {...register(`collections.${index}.sex`, { ...validationSchema.collections.sex })}
            />
            <label htmlFor={`collections.${index}.sex_${value}`} className="pointer">
              {label}
            </label>
          </div>
        ))}
      </div>
      <div>
        <label>D.O.B</label>
        <Controller
          name={`collections.${index}.dob`}
          control={control}
          rules={{ ...validationSchema.collections.dob }}
          render={({ field: { onChange, value } }) => (
            <DatePickerNative
              value={value}
              max={DateUtils.dateNow()}
              style={{ marginBottom: 0 }}
              onChange={onChange}
              testId={`cage-${index}-dob-input`}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={`collections.${index}.dob`}
          render={({ message }) => <p className="f7 red db lh-copy pt1">{message}</p>}
        />
      </div>
    </div>
  );
});

export default CageRow;
